import { animation } from '@design-system/styles'
import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'

import { _Badge } from '@/component/atoms/Badge'
import { Icon } from '@/component/atoms/Icon'
import NotificationService from '@/contentComponents/global/Notification/Notification.service'
import useRepository from '@/hooks/useRepository'
import { podMallCartService } from '@/page/PODMall/pages/PODMallCart/_service/PODMallCart.service'
import PODMallCouponService from '@/page/PODMall/pages/PODMallManage/PODMallCoupon/_service/PODMallCoupon.service'
import commonRepo from '@/repository/common.repo'
import { RouteName, 내신준비기간 } from '@/router'
import { localStorageService } from '@/service/storage.service'
import navigationUtils from '@/utils/navigationUtils'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'

import HeaderMenuPopover from './HeaderMenuPopover'
import S from './HeaderUtil.style'
import Profile from './Profile'

const getIsSchoolExamZoneGuideTooltipOpened = () => {
  return !localStorageService.getItem('SCHOOL_EXAM_ZONE_GUIDE_TOOLTIP_OPENED')
}

const HeaderUtil = () => {
  const notificationService = useRepository(NotificationService)
  const podCouponService = useRepository(PODMallCouponService)
  const history = useHistory()

  const [beforeUnreadCount, setBeforeUnreadCount] = useState(Number.MAX_SAFE_INTEGER)
  const [ringAnimation, setRingAnimation] = useState(false)
  const [isSchoolExamZoneGuideTooltipOpened, setIsSchoolExamZoneGuideTooltipOpened] = useState(
    getIsSchoolExamZoneGuideTooltipOpened(),
  )

  const ref = useRef<NodeJS.Timeout | null>(null)

  const closeSchoolExamZoneGuideTooltip = () => {
    localStorageService.setItem('SCHOOL_EXAM_ZONE_GUIDE_TOOLTIP_OPENED')(Date.now())
    setIsSchoolExamZoneGuideTooltipOpened(getIsSchoolExamZoneGuideTooltipOpened())
  }

  const isGuidePage = useRouteMatch(RouteName.guide)

  useEffect(() => {
    setBeforeUnreadCount(notificationService.unreadCount)
    if (beforeUnreadCount !== -1 && beforeUnreadCount < notificationService.unreadCount) {
      setRingAnimation(true)
      return
    }

    ref.current = setTimeout(() => {
      setRingAnimation(false)
      ref.current = null
    }, 1500)
  }, [notificationService.unreadCount])

  useEffect(() => {
    return () => {
      if (ref.current) {
        clearTimeout(ref.current)
      }
    }
  }, [])

  useEffect(() => {
    podMallCartService.fetchSetCartItems()
    podCouponService.fetchSetCouponList()
  }, [])

  return (
    <div css={S.Wrap}>
      {commonRepo.permission.get.schoolExamZone.access && (
        <div
          className="header__school-exam-zone-badge-container"
          onClick={() => history.push(RouteName.schoolExamZone)}
        >
          <_Badge
            color="var(--Colors-Orange-500)"
            background="var(--Colors-Orange-100)"
            size="large"
            className={clsx(
              'header-school-exam-zone-badge',
              history.location.pathname === RouteName.schoolExamZone && 'active',
            )}
          >
            내신관
          </_Badge>
          {!commonRepo.isSchoolflat && 내신준비기간 && isSchoolExamZoneGuideTooltipOpened && (
            <div css={[S.tooltipFolding, S.schoolExamZoneGuide]}>
              <p>
                이번 시험,
                <br />
                내신관으로 완벽 대비해요.
              </p>
              <button
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation()
                  closeSchoolExamZoneGuideTooltip()
                }}
              >
                <Icon name="close" size={16} color="var(--Colors-Gray-600)" />
              </button>
            </div>
          )}
        </div>
      )}
      {commonRepo.isSchoolflat && (
        <button
          className={clsx('schoolflat-guide-button', isGuidePage && 'current')}
          onClick={() => history.push(RouteName.guide)}
        >
          스쿨플랫 가이드
        </button>
      )}
      {commonRepo.permission.get.orderManage.access && (
        <div
          style={{
            position: 'relative',
          }}
        >
          <HeaderMenuPopover />
        </div>
      )}
      <button
        data-component="새로고침버튼"
        className="flex handler-button"
        onClick={() => navigationUtils.reload()}
      >
        <div className="icon__wrapper button__refresh">
          <Icon name="refresh" size={20} color={colors.gray.$700} />
        </div>
      </button>
      {commonRepo.permission.get.notification.access && (
        <button
          className="flex handler-button"
          onClick={() => {
            notificationService.open()
            _mixpanelUtils.uiTrack('GNB_알림아이콘btn')
          }}
        >
          <div className="icon__wrapper">
            <Icon name="notification" size={20} color={colors.gray.$700} />
            {notificationService.unreadCount > 0 && (
              <p
                className={clsx(
                  'count-badge',
                  'noti-badge',
                  notificationService.unreadCount >= 99 && 'max-count',
                )}
                css={ringAnimation && animation.pulse}
              >
                {notificationService.unreadCountWithLimit}
              </p>
            )}
          </div>
        </button>
      )}
      {commonRepo.permission.get.myinfo.access && (
        <div css={S.basic} data-component="유저페이지 이동">
          <Profile />
        </div>
      )}
    </div>
  )
}

export default observer(HeaderUtil)
